/// <reference path="../../../../WebUI/typings/tsd.d.ts" />
/// <reference path="../../Views/LogoutPanel.tsx" />

import * as React from "react";
import * as ReactDOM from "react-dom";
import Rest = require("../../Generated/RestClient");
import * as Views from "../Views/MasterPage";
import * as Dialogs from "../../Dialogs/MessageBox";
import { resourceStrings, NavLinks } from "../../../../Intercom.ReactUI/Views/index";
import { Resources } from "../../Resources/Intercom.Resources.DevPortal";
import { DropdownMenu } from "../../Views/DropdownMenu";
import { clientAppSettings } from "../../Models/ClientAppSettings";
import { BotProgress } from "../Views/index";
import { DevPortalAppBase, IAnnouncements } from "../../DevPortalAppBase";
import isInFrame from "../../../../Intercom.ReactUI/Utils/InFrame";
import { Environment } from "../../../../Intercom.ReactUI/Utils/Environment";
import { relativeUrl } from "../../Utils/Links";
import { SupportedFeatureUtility } from "../Utility/SupportedFeatureUtility";
import logger from "../../../../Intercom.ReactUI/Utils/logger";
import { FlightUtils } from '../../Utils/FlightUtils';
import { parseApiError } from "../Services/parseApiError";
import { HeaderKind } from "../Utility/HeaderKind";
import { Environment as PortalEnvironment } from "../../Utils/Environment";

import { AuthenticationUtility } from "../Utility/AuthenticationUtility";
import { AzurePortalMessenger, InfoResult, AccessTokensResult, EndpointStatusResult, AppInsightsStatusResult, DefaultPortalSignature } from "../Utility/AzurePortalMessenger";

export function isBrowserSupported(): boolean {
    /*
    // check result of parseUserAgent against KnownBrowsers to decide
    // whether we can support the browser or not. Currently we support all browsers
    // ideally we should use browserCaps instead of userAgent to decide
    let agent = parseUserAgent();
    if (agent[KnownBrowser[KnownBrowser.{UnSupportedBrowserBrand}]])
        return false;
    */
    return true;
}

export function refreshClientAppSettings(): JQueryPromise<Rest.ClientAppSettings> {
    return Rest.AccountClient.getClientSettings().done(settings => {
        _.extend(clientAppSettings, settings, _.pick(clientAppSettings, 'homeUrl'));
    });
}

/**
 * Current page status - these are ONLY valid when loading from Azure Ibiza.
 */
export enum PageStatusFlags {
    Ready = 0,
    LoadingBot = 1,
    LoggingIn = 1 << 1,
    WaitingForBotReady = 1 << 2,
    WaitingForAppInsightsReady = 1 << 3
}

/**
 * This is the base class for all controllers in this app.
 * Here we render parts common to all pages, and handle events in those parts.
 */
export class ControllerBase extends SimpleMvc.Controller {
    protected masterPageState: Views.MasterPageState;
    protected params: SimpleMvc.QueryParams;
    protected masterPage: Views.MasterPage;
    private accessToken: string;
    protected containerController: ControllerBase;
    private handlersAttached = false;
    private activeDropdownMenu: DropdownMenu = null;
    /** Page status - this is only used by Azure Ibiza and should be accessed using the set/clear/hasPageStatusFlag methods. */
    private _pageStatus: PageStatusFlags = PageStatusFlags.Ready;

    private _azurePortalMessenger: AzurePortalMessenger;
    private _onReceiveInfo: (info: InfoResult) => void;
    private _onReceiveAzureToken: (tokens: AccessTokensResult) => void;
    private _onReceiveEndpointStatus: (status: EndpointStatusResult) => void;
    private _onReceiveAppInsightsStatus: (status: AppInsightsStatusResult) => void;
    private _appInsightsStatusTimer: number;

    protected IsUserMSA() : boolean {
        return this.app.signedInUser && this.app.signedInUser.IdentityProvider === 'live.com';
    }

    protected setPageStatusFlag(flag: PageStatusFlags): boolean {
        const hasFlag = this.hasPageStatusFlag(flag);
        this._pageStatus |= flag;

        return hasFlag;
    }

    protected clearPageStatusFlag(flag: PageStatusFlags): boolean {
        const hasFlag = this.hasPageStatusFlag(flag);
        this._pageStatus &= ~flag;

        return hasFlag;
    }

    protected hasPageStatusFlag(flag: PageStatusFlags): boolean {
        return (this._pageStatus & flag) === flag;
    }

    protected setBotAppSettings(x: any) {
        if (this.containerController) {
            this.containerController.setBotAppSettings(x);
        }
    }

    public isLoaded(): boolean {
        return this.containerController ? this.containerController.isLoaded() : super.isLoaded();
    }

    public get pageContent() {
        return this.containerController ? this.containerController.pageContent : this.masterPage.pageContent;
    }

    constructor(public app: DevPortalAppBase, protected headerKind = HeaderKind.Small) {
        super();
    }

    private hideActiveDropdownMenu() {
        if (!this.activeDropdownMenu) return;
        this.activeDropdownMenu.hide();
        this.activeDropdownMenu = null;
    }

    public isAdminOrSupport(): boolean {
        const env = PortalEnvironment.portalEnvironment();
        if (env == "scratch" || env == "ppe") {
            return this.app.signedInUser != null && this.app.signedInUser.Roles != null && (this.app.signedInUser.Roles.indexOf('IntAdmin') >= 0 || this.app.signedInUser.Roles.indexOf('IntSupport') >= 0);
        } else {
            return this.app.signedInUser != null && this.app.signedInUser.Roles != null && (this.app.signedInUser.Roles.indexOf('ProdAdmin') >= 0 || this.app.signedInUser.Roles.indexOf('ProdSupport') >= 0);
        }
    }

    public isReviewer(): boolean {
        return this.app.signedInUser != null && this.app.signedInUser.Roles.indexOf('reviewer') >= 0;
    }

    public setSearchState(search: string, searchOpened: boolean) {
        this.masterPage.setState(_.extend(this.masterPageState, { searchString: search, expandSearch: searchOpened }));
    }

    public setAnnouncementState(announcements: IAnnouncements) {
        if (this.masterPage) {
            this.masterPage.setState({ announcements } as Views.MasterPageState);
        }
    }

    public load(params: SimpleMvc.QueryParams): void {
        super.load(params);
        this.detachHandlers();

        this.params = params;

        ControllerBase.setEnvironment(this.params);

        if (Environment.isInBotFrameworkAzureExtension) {
            this._initializeForAzure();
        }

        if (this.pageRequiresSignIn()) {
            if (!isBrowserSupported()) {
                this.renderCommon(true);
                this.updateState(false, null, () => {
                    // do nothing
                });
            } else if (this.app.signedInUser) {
                this.clearPageStatusFlag(PageStatusFlags.LoggingIn);
                this.renderCommon();
                this.updateState(false, null, () => {
                    this.render(params);
                });
            }
            else {
                Rest.AccountClient.getSignedInUser().done(user => {
                    if (this.needSignInAndCanRedirect(user)) {
                        // If we can, ask the user to sign-in again.
                        const signinUri = this.getSignInUrl();
                        window.location.replace(signinUri);
                    } else {
                        logger.debug('ControllerBase: Signed in user via DevPortal. Setting and refreshing view.');

                        this.app.signedInUser = user;
                        if (!AuthenticationUtility.setRequestVerificationHeader()) {
                            logger.debug("ControllerBase: Failed to set request verification header when getting signed in user.");
                        }

                        this.clearPageStatusFlag(PageStatusFlags.LoggingIn);

                        this.renderCommon();
                        refreshClientAppSettings().then(
                            () => {
                                this.updateState(false, null, () => this.render(params));
                            }
                        );
                    }
                }).fail(() => {
                    if (isInFrame() || Environment.isInBotFrameworkAzureExtension) {
                        // We are in azure portal as iframe - wait for cross-frame message to log us in.
                        logger.debug('ControllerBase: Waiting for cross-frame message');
                    } else {
                        this.app.signedInUser = null;
                        this.renderCommon(true);
                    }
                });
            }
        }
        else {
            // check browser support and block if we are hosted by azure portal
            this.renderCommon(isInFrame());
            this.showProgressMask = false;
            this.updateState(false, null, () => {
                this.render(params);
            });
        }

        this.addRemoveCssClass(true);

        // Load tracking code here if the user consent to our cookie policy
        this.app.trackingCookieConsent.loadPageTrackingIfInformed();
    }

    /**
     * Do we need to sign the given user in and can we redirect to the sign-in page?
     * @param user - Current logged in user.
     */
    protected needSignInAndCanRedirect(user: Rest.AuthData): boolean {
        const canRedirect = !isInFrame() && !Environment.isInBotFrameworkAzureExtension;
        const needSignIn = (FlightUtils.IsFirstPartyAuthFlighted || (user.FlightedFeatures && user.FlightedFeatures.indexOf('firstPartyAuth') >= 0)) && !this.isBfAppId(user);

        return canRedirect && needSignIn;
    }

    private isBfAppId(user: Rest.AuthData): boolean {
        const bfAppId = clientAppSettings.appId.toUpperCase();
        const isBfApp = user.AppId.toUpperCase() === bfAppId;
        return isBfApp;
    }

    protected getSignInUrl(): string {
        const paramValue = encodeURIComponent(relativeUrl(window.location));
        return `/Identity/SignIn?requestUrl=${paramValue}`;
    }

    public unload(): void {
        if (this._appInsightsStatusTimer) {
            clearTimeout(this._appInsightsStatusTimer);
            this._appInsightsStatusTimer = undefined;
        }
        if (this._onReceiveInfo) {
            this._azurePortalMessenger.unlistenForInfo(this._onReceiveInfo);
            this._onReceiveInfo = undefined;
        }
        if (this._onReceiveAzureToken) {
            this._azurePortalMessenger.unlistenForAuthenticationTokens(this._onReceiveAzureToken);
            this._onReceiveAzureToken = undefined;
        }
        if (this._onReceiveEndpointStatus) {
            this._azurePortalMessenger.unlistenForEndpointStatus(this._onReceiveEndpointStatus);
            this._onReceiveEndpointStatus = undefined;
        }
        if (this._onReceiveAppInsightsStatus) {
            this._azurePortalMessenger.unlistenForAppInsightsStatus(this._onReceiveAppInsightsStatus);
            this._onReceiveAppInsightsStatus = undefined;
        }
        if (this._azurePortalMessenger) {
            this._azurePortalMessenger = undefined;
        }

        this.statusBar = null;
        this.showProgressMask = false;
        this.detachHandlers();
        this.hideActiveDropdownMenu();
        this.addRemoveCssClass(false);
        this.app.trackingCookieConsent.markAsInformed();
    }

    private addRemoveCssClass(add: boolean): void {
        const cssClass = this.getCssClass();
        if (cssClass) {
            const $appBody = $(this.app.getAppBody());
            if (add) {
                $appBody.addClass(cssClass);
            }
            else {
                $appBody.removeClass(cssClass);
            }
        }
    }

    /**
     *  Controllers that want to add a custom CSS class to app-body any time they are loaded should override this method.
     */
    protected getCssClass(): string {
        return null;
    }

    protected pageRequiresSignIn(): boolean {
        return true;
    }

    protected refreshSignedInUser() {
        // synchronize csrf cookie and header value
        if (!AuthenticationUtility.setRequestVerificationHeader()) {
            logger.debug("ControllerBase: Failed to set request verification header when refreshing signed in user.");
        }
        if (!this.app.signedInUser) {
            logger.debug('begin refresh signed user');
            this.load(this.params);
        }
    }

    /**
     * Renders parts common to all pages.
     * This method is called whether or not the user is signed in.
     */
    protected renderCommon(checkBrowserSupport?: boolean): void {
        const props: Views.MasterPageProps = {
            controller: this,
            isAdmin: () => this.isAdminOrSupport(),
            isReviewer: () => this.isReviewer(),
            signInRequired: this.pageRequiresSignIn(),
            onSearch: (s) => this.handleSearch(s),
            onSignOutClicked: () => this.onSignOutClicked(),
            onUserAccountClicked: () => this.onUserAccountClicked(),
            refreshSignedInUser: () => this.refreshSignedInUser(),
            headerKind: this.headerKind,
            isRoutableUrl: (url: string) => {
                return !(/^https?:/i.test(url) || /^\/\//.test(url));
            }
        };
        const element = React.createElement(Views.MasterPage, props);
        this.masterPage = ReactDOM.render<Views.MasterPageProps, Views.MasterPage>(element, this.app.getAppBody(), () => {
            this.attachHandlers();
        }) as Views.MasterPage;

        const state = {
            unsupportedBrowser: checkBrowserSupport && !isBrowserSupported(),
            announcements: this.app.announcements
        } as Views.MasterPageState;

        if (Environment.isInBotFrameworkAzureExtension) {
            logger.debug("ControllerBase: Rendering with azure page state: " + this._pageStatus);

            const statusMessage = this._getStatusBarMessageFromPageStatus();
            if (statusMessage) {
                state.statusBar = React.createElement(BotProgress, { progress: statusMessage });
            }
            else {
                state.statusBar = undefined;
            }
        }

        this.masterPage.setState(state);
    }

    private _getStatusBarMessageFromPageStatus(): string {
        if (this.hasPageStatusFlag(PageStatusFlags.LoggingIn)) {
            return Resources.loadingBot;
        }
        if (this.hasPageStatusFlag(PageStatusFlags.LoadingBot)) {
            return Resources.loadingBot;
        }
        if (this.hasPageStatusFlag(PageStatusFlags.WaitingForBotReady)) {
            return Resources.botIsNotReady;
        }
        if (this.hasPageStatusFlag(PageStatusFlags.WaitingForAppInsightsReady)) {
            return Resources.analyticsNotReady;
        }
        return undefined;
    }

    protected get statusBar(): JSX.Element {
        if (this.containerController) {
            return this.containerController.statusBar;
        } else {
            return this.masterPage.state.statusBar;
        }
    }

    protected set statusBar(bar: JSX.Element) {
        if (Environment.isInBotFrameworkAzureExtension) {
            // In Azure Ibiza the status bar is controlled via the _pageStatus instead of being set directly.
            // To reduce refactoring we are just short circuiting other code for now.
            return;
        }

        if (this.containerController) {
            this.containerController.statusBar = bar;
        } else {
            this.masterPage.setState({ statusBar: bar, statusBarScrolls: false });
        }
    }

    protected set showProgressMask(show: boolean) {
        if (this.containerController) {
            this.containerController.showProgressMask = show;
        } else {
            this.masterPage.setState({ showProgressMask: show } as any);
        }
    }

    protected updateTitle(title: string) {
        $('head title').text(title || resourceStrings.botFrameworkExtended);
    }

    /**
     * This method is called if the user is signed in.
     * Subclasses should override this method.
     */
    protected render(params: SimpleMvc.QueryParams): void {
        this.updateTitle(null);
        $('.action-sheet').toggleClass("hidden", true);
    }

    protected updateState(signInInProgress: boolean, signInError: string, callback?: () => void): void {
        const curActiveLink = this.masterPageState ? this.masterPageState.activeLink : NavLinks.root;

        this.masterPageState = _.extend({}, this.masterPageState, {
            signInState: this.masterPageState ? this.masterPageState.signInState : null,
            signedInUser: this.app.signedInUser,
            signInInProgress: signInInProgress,
            signInError: signInError,
            activeLink: curActiveLink,
            searchString: null
        });

        this.masterPage.setState(this.masterPageState, () => {
            if (callback)
                callback();
        });
    }

    protected setActiveJumboActiveLink(activeLink: NavLinks) {
        if (this.masterPage) {
            this.masterPageState.activeLink = activeLink;
            this.masterPage.setState(this.masterPageState);
        }
    }

    protected handleSearch(s: string) {
        if (s.trim()) {
            this.app.navigate(clientAppSettings.searchRoute || '/bots', { s: s });
        }
    }

    protected get azureAccessToken(): string {
        let token = this.app.signedInUser ? this.app.signedInUser.AzureToken : null;
        if (token == null && window.location.host === 'localhost:3003') {
            token = sessionStorage.getItem('BF_OAUTH_TOKEN')
        }
        return token;
    }

    private attachHandlers(): void {
        if (this.handlersAttached) return;
        this.handlersAttached = true;

        const $appBody = $(this.app.getAppBody());
        $appBody.on('click', '.signed-in-user', () => this.onSignedInUserClicked());
        $appBody.on('click', '.header-announcement', () => this.onAnnouncementsClicked());
        $appBody.on('click', '.waitlist-request', () => this.onWaitListButtonClicked());
    }

    private detachHandlers(): void {
        const $appBody = $(this.app.getAppBody());
        $appBody.off();
        this.handlersAttached = false;
    }

    private extractErrorFromXHR(xhr: JQueryXHR): string {
        const apiErr = parseApiError(xhr);
        if (apiErr && apiErr.message) {
            return apiErr.message;
        }
        if (xhr && xhr.responseText) {
            try {
                let error = $.parseJSON(xhr.responseText);
                if (error) {
                    error = error.Errors && error.Errors.length > 0 ? error.Errors[0] : error;
                    const message = error.Message || (error.Error ? error.Error.Message : null);
                    if (message) {
                        return message;
                    }
                }
            }
            catch (ex) {
                // Ignore parsing errors, we want to show users the original error.
            }
        }
        return null;
    }

    protected handleHttpResponseError(xhr: JQueryXHR): JQueryPromise<any> {
        const errorMessage = this.extractErrorFromXHR(xhr);
        let promise: JQueryPromise<any>;

        if (errorMessage) {
            promise = Dialogs.MessageBox.show(errorMessage);
        }
        else {
            switch (xhr.status) {
                case 400:
                    promise = Dialogs.MessageBox.show(Resources.xhrResponse_400);
                    break;
                case 401: {
                    const deferred = $.Deferred();
                    this.app.signedInUser = null;
                    if (this.isLoaded()) {
                        this.updateState(false, null, () => { deferred.resolve(null); });
                    } else {
                        deferred.resolve(null);
                    }
                    promise = deferred.promise();
                    break;
                }
                case 403:
                    promise = Dialogs.MessageBox.show(Resources.xhrResponse_403);
                    break;
                case 404:
                    promise = Dialogs.MessageBox.show(Resources.xhrResponse_404);
                    break;
                case 500:
                    promise = Dialogs.MessageBox.show(Resources.xhrResponse_500);
                    break;
                case 501: // Not implemented
                    promise = Dialogs.MessageBox.show(Resources.xhrResponse_501);
                    break;
                default:
                    if (xhr.statusText)
                        promise = Dialogs.MessageBox.show(Resources.xhrResponse_Unknown.replace("%1", xhr.statusText));
                    else
                        promise = Dialogs.MessageBox.show(Resources.xhrResponse_Unknown);
                    break;
            }
        }
        return promise;
    }

    public redirectIfNeeded(): boolean {
        const prefix = "?appapiret=";
        const index = window.location.href.indexOf(prefix);
        if (index === -1)
            return false;
        const ret = window.location.href.substr(index + prefix.length);
        if (ret) {
            window.location.assign(decodeURIComponent(ret));
            return true;
        }
        return false;
    }

    private onSignedInUserClicked(): void {
        const userMenu = <DropdownMenu>this.masterPage.pageHeader.refs["userMenu"];
        const wasThisMenu = this.activeDropdownMenu === userMenu && this.activeDropdownMenu.state.visible;
        this.hideActiveDropdownMenu();
        if (!wasThisMenu) {
            this.activeDropdownMenu = userMenu;
            this.activeDropdownMenu.displayBelow($('.signed-in-user'), $('.rhs'));
        }
    }

    private onAnnouncementsClicked() {
        const announcementsMenu = <DropdownMenu>this.masterPage.pageHeader.refs["announcementsMenu"];
        const wasThisMenu = this.activeDropdownMenu === announcementsMenu && this.activeDropdownMenu.state.visible;
        this.hideActiveDropdownMenu();
        if (!wasThisMenu) {
            this.activeDropdownMenu = announcementsMenu;
            this.activeDropdownMenu.displayBelow($('.header-announcement'), $('.rhs'));
            this.app.announcements.addShown();
        }
    }

    /**
     * Removes unwanted characters from the message.
     */
    private cleanupErrorMessage(message: string): string {
        // Note that we don't want to decode the string because most of the time that will result in double decoding.
        return message.replace(/\+/g, ' ');
    }

    /**
        * This method return false for default error message display to happen.
        */
    private handleIntercomLoginError(xhr: JQueryXHR, accessToken: string): boolean {
        const signInState = xhr.responseJSON.Message;
        switch (signInState) {
            case "SignedUp":
            case "NotSignedUp":
                this.accessToken = accessToken;
                this.masterPageState.signInState = signInState;
                this.updateState(false, null);
                return true;
        }
        return false;
    }

    private onWaitListButtonClicked(): void {
        Rest.AccountClient.addUserToWaitlist().done(() => {
            this.masterPageState.signInState = "SignedUp";
            this.updateState(false, null);
        }).fail(xhr => {
            this.handleHttpResponseError(xhr);
        });
    }

    private onSignOutClicked(): void {
        const path = clientAppSettings.applicationPath === '/' ? '' : clientAppSettings.applicationPath;
        window.location.assign(path + '/signout_all.aspx');
    }

    private onUserAccountClicked(): void {
        const deleteAccountAllowed = SupportedFeatureUtility.isSupported(Rest.DevPortalFeatureFlags.SupportNonAzureUsers);
        if (deleteAccountAllowed) {
            window.location.href = '/account';
        }
    }

    public scrollToTop(): void {
        if (this.masterPage) {
            this.masterPage.pageContent.scrollTop = 0;
        }
    }

    public hasUnsavedChanges(): boolean {
        return false;
    }

    private static setEnvironment(queryParams: SimpleMvc.QueryParams) {
        const environmentQueryStringKey: string = "ioenv";
        if (!queryParams || !queryParams.hasOwnProperty(environmentQueryStringKey)) {
            return;
        }

        const environmentQueryStringValue = queryParams[environmentQueryStringKey];
        if (environmentQueryStringValue.toLowerCase() === "ibiza") {
            Environment.isInBotFrameworkAzureExtension = true;
        }
    }

    /**
     * Is this the webchat route?
     */
    protected isWebChatRoute(path: string): boolean {
        const normalizedPath = path ? path.toLowerCase().trim() : "";
        return normalizedPath === "/bots/webchat";
    }

    /**
     * Is this the analytics route?
     */
    private _isAnalyticsRoute(path: string): boolean {
        const normalizedPath = path ? path.toLowerCase().trim() : "";
        return normalizedPath === "/bots/analytics";
    }

    private _initializeForAzure() {
        this.headerKind = HeaderKind.None;

        // Load is re-entrant - do not create the messenger 2x.
        if (!this._azurePortalMessenger) {
            // If we think we are in azure but don't have a trusted authority we are probably running scratch locally and have just added the querystring to flag running in Azure.
            // Without a trusted authority we don't accept/send messages.
            const matches = decodeURIComponent(window.location.search).match(/trustedAuthority=([^&]+)/);
            if (matches) {
                logger.debug("ControllerBase: Starting to listen to Azure cross frame messages");

                const trustedAuthority = matches[1];
                const signatureMatches = decodeURIComponent(window.location.search).match(/framesignature=([^&]+)/);
                let signature = DefaultPortalSignature;
                if (signatureMatches) {
                    signature = signatureMatches[1];
                }
                this._azurePortalMessenger = new AzurePortalMessenger(trustedAuthority, signature);

                // If we are in web chat wait for the bot to be ready for messages before opening.
                const blockUIUntilBotReady = this.isWebChatRoute(this.app.getCurrentPagePath());
                if (blockUIUntilBotReady) {
                    this.setPageStatusFlag(PageStatusFlags.WaitingForBotReady);

                    this._onReceiveEndpointStatus = this._refreshViewBasedOnEndpointStatus.bind(this);
                    this._azurePortalMessenger.listenForEndpointStatus(this._onReceiveEndpointStatus);
                }
                // If we are showing app insights data wait for that to be ready before opening.
                const blockUIUntilAppInsightsReady = this._isAnalyticsRoute(this.app.getCurrentPagePath());
                if (blockUIUntilAppInsightsReady) {
                    this.setPageStatusFlag(PageStatusFlags.WaitingForAppInsightsReady);

                    this._appInsightsStatusTimer = setTimeout(() => {
                        logger.debug("ControllerBase: Failed to get app insights status fast enough. Clearing wait status.");
                        this._appInsightsStatusTimer = undefined;

                        if (this.clearPageStatusFlag(PageStatusFlags.WaitingForAppInsightsReady)) {
                            this.renderCommon();
                        }
                    }, 2 * 60 * 1000);

                    this._onReceiveAppInsightsStatus = this._refreshViewBasedOnAppInsightsStatus.bind(this);
                    this._azurePortalMessenger.listenForAppInsightsStatus(this._onReceiveAppInsightsStatus);
                }

                this.setPageStatusFlag(PageStatusFlags.LoggingIn);

                this._onReceiveInfo = this._onReceiveInfoFromIFrame.bind(this);
                this._azurePortalMessenger.listenForInfo(this._onReceiveInfo);

                // Listen for auth tokens - these can be refreshed periodically without corresponding requestAuthenticationTokens calls.
                this._onReceiveAzureToken = this._refreshAuthenticationTokens.bind(this);
                this._azurePortalMessenger.listenForAuthenticationTokens(this._onReceiveAzureToken);

                // Tell Azure we are ready to get messages.
                this._azurePortalMessenger.postReady();
                this._azurePortalMessenger.initializationComplete();

                // Request all the info we need and trigger auto-refresh of that data.
                this._azurePortalMessenger.requestInfo();
                this._azurePortalMessenger.requestAuthenticationTokens();
                if (blockUIUntilBotReady) {
                    this._azurePortalMessenger.requestEndpointStatus();
                }
                if (blockUIUntilAppInsightsReady) {
                    this._azurePortalMessenger.requestAppInsightsStatus();
                }
            }
        }
    }

    private _onReceiveInfoFromIFrame(info: InfoResult): void {
        // We can do something here in the future based on the version... for now just output the value.
        logger.debug("ControllerBase: Got Azure Extension version: " + info.version);
        this._azurePortalMessenger.unlistenForInfo(this._onReceiveInfo);
        this._onReceiveInfo = undefined;
    }

    private _handleRefreshAuthenticationFailure(): void {
        this.app.signedInUser = null;
        Dialogs.MessageBox.show(Resources.refreshSignInFailure);
    }

    /**
     * Callback whenever we get authentication tokens from the Azure portal. This refreshes the local tokens/user info.
     */
     private _refreshAuthenticationTokens(tokens: AccessTokensResult): void {
        const armToken = tokens.armToken;
        const selfToken = tokens.selfToken;

        const useSelfToken = selfToken && clientAppSettings.flightedFeatures.indexOf("firstPartyAuth") >= 0;
        const token = useSelfToken ? selfToken : armToken;
        const csrfToken = AuthenticationUtility.getCsrfToken();
        if (!csrfToken) {
            logger.debug("ControllerBase: Failed to get CSRF token from cookie.");
            this._handleRefreshAuthenticationFailure();
            return;
        }

        logger.debug("ControllerBase: Azure sent us Authentication tokens - Refreshing.");
        $.ajax("/refreshLogin", {
            data: { token, '__RequestVerificationToken': csrfToken },
            type: "POST"
        })
        .then(() => {
            Rest.ajaxHeaders = _.extend(Rest.ajaxHeaders || {}, { Authorization: `Bearer ${token}`});
            // Get the signed in user.
            logger.debug("ControllerBase: Getting signed in user.");
            return Rest.AccountClient.getSignedInUser();
        })
        .then((authData: Rest.AuthData) => {
            logger.debug("ControllerBase: Got authenticated user.");

            if (this.app.signedInUser) {
                // Already logged in, just refresh the token.
                // Future: Bypass the getSignedInUser call - leaving as is for now.
                AuthenticationUtility.bindAzureAccessToken(this.app.signedInUser, armToken, true);

                if (this.clearPageStatusFlag(PageStatusFlags.LoggingIn)) {
                    this.renderCommon();
                }
            }
            else {
                // New user, assign user and re-render the page.
                this.app.signedInUser = authData;
                AuthenticationUtility.bindAzureAccessToken(this.app.signedInUser, armToken, true);

                this.clearPageStatusFlag(PageStatusFlags.LoggingIn);
                this.renderCommon();
                // Now that they are logged in need to refresh client settings to get any info that may be flighted to their account (ie channels)
                refreshClientAppSettings().then( () =>
                    this.updateState(false, null, () => {
                        this.render(this.params);
                    })
                );
            }
        })
        .fail(xhr => {
            if (this.clearPageStatusFlag(PageStatusFlags.LoggingIn)) {
                this.renderCommon();
            }

            this.handleHttpResponseError(xhr);
        });
    }

    private _refreshViewBasedOnEndpointStatus(status: EndpointStatusResult): void {
        logger.debug("ControllerBase: Got new bot status from Azure: " + status.status);

        let stateChanged: boolean;
        switch (status.status) {
            case "not-ready":
                stateChanged = this.setPageStatusFlag(PageStatusFlags.WaitingForBotReady);
                break;
            case "ready":
            case "no-endpoint":
            case "error":
            default:
                stateChanged = this.clearPageStatusFlag(PageStatusFlags.WaitingForBotReady);
                break;
        }

        if (stateChanged) {
            this.renderCommon();
        }
    }

    private _refreshViewBasedOnAppInsightsStatus(status: AppInsightsStatusResult): void {
        logger.debug("ControllerBase: Got new app insights status from Azure: " + status.status);

        let stateChanged: boolean;
        switch (status.status) {
            case "not-ready":
                // If we gave up waiting for Azure to tell us app insights is ready then ignore the "not-ready" status.
                if (this._appInsightsStatusTimer) {
                    stateChanged = this.setPageStatusFlag(PageStatusFlags.WaitingForAppInsightsReady);
                }
                break;
            case "ready":
            case "not-configured":
            case "error":
                stateChanged = this.clearPageStatusFlag(PageStatusFlags.WaitingForAppInsightsReady);
                default:
        }

        if (stateChanged) {
            this.renderCommon();
        }
    }
}



// WEBPACK FOOTER //
// ./Client/v4/Controllers/ControllerBase.ts